import { Button } from '@viastud/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@viastud/ui/dialog'
import { SubscriptionUserCard } from '@viastud/ui/shared/subscription-user-card'
import type React from 'react'
import { useState } from 'react'

import { UnitaryCourseCard } from '@/components/settings/unitary-course-card.tsx'
import type { StudentSubscriptionInfo } from '@/presenters/student-subscription.presenter.ts'
import { useAuthStore } from '@/store/auth.store.ts'

interface StudentSubscriptionCardProps {
  subscription: StudentSubscriptionInfo
  onCancel: () => void
  onRePay: () => void
  onRenew: () => void
  isActionLoading: boolean
  isLegacyPrice: boolean
}

export const StudentSubscriptionCard: React.FC<StudentSubscriptionCardProps> = ({
  subscription,
  onCancel,
  onRenew,
  onRePay,
  isActionLoading,
  isLegacyPrice,
}) => {
  const { user } = useAuthStore()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [actionType, setActionType] = useState<'cancel' | 'renew' | null>(null)

  const handleOpenModal = (type: 'cancel' | 'renew') => {
    setActionType(type)
    setIsModalOpen(true)
  }

  const handleConfirm = () => {
    if (!actionType) return

    if (actionType === 'cancel') {
      onCancel()
    } else {
      onRenew()
    }
    setIsModalOpen(false)
    setActionType(null)
  }

  return (
    <div className="flex h-full flex-col gap-x-6 space-y-6 md:flex-row">
      <div className="flex h-full w-full flex-col gap-y-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mon abonnement</h2>
        </div>
        {user && (
          <SubscriptionUserCard
            key={user.id}
            child={{
              id: user.id,
              firstName: user.firstName,
              lastName: user.lastName,
              status: subscription.status,
              endDate: subscription.endDate,
              nextBilling: subscription.nextBilling,
              isSubscribed: user.hasSubscription,
              hasParent: !!user.parentId,
              commitment: subscription.commitment,
              commitmentEndDate: subscription.commitmentEndDate,
            }}
            onCancel={() => {
              handleOpenModal('cancel')
            }}
            onRenew={() => {
              handleOpenModal('renew')
            }}
            onRePay={onRePay}
            isActionLoading={isActionLoading}
          />
        )}
      </div>
      <div className="w-full">
        <h2 className="mb-4 text-2xl font-bold text-gray-900">Mes cours</h2>
        <UnitaryCourseCard
          userGrade={user?.grade ?? ''}
          hasSubscription={['active', 'cancelled'].includes(subscription.status)}
        />
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsModalOpen(false)
            setActionType(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'cancel' ? "Annuler l'abonnement" : "Réactiver l'abonnement"}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'cancel'
                ? `Es-tu sûr de vouloir annuler ton abonnement ? Il restera actif jusqu'à la fin de la période en cours.`
                : `Veux-tu réactiver ton abonnement ?`}
            </DialogDescription>
            {actionType === 'cancel' && isLegacyPrice && (
              <p className="text-sm font-medium text-amber-700">
                Attention : tu bénéficies actuellement de l&apos;ancien tarif de l&apos;abonnement.
                Tu peux réactiver ton abonnement et conserver ce tarif jusqu&apos;à la fin de la
                période en cours. Passé cette date, si tu te réabonnes, tu paieras le tarif actuel.
              </p>
            )}
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsModalOpen(false)
                setActionType(null)
              }}
            >
              Annuler
            </Button>
            <Button
              variant={actionType === 'cancel' ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={isActionLoading}
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
